/* @flow */

import type { Profile, ProfileList, Profiles } from './typesAndConstants';

import { deleteSearchHistory } from '../../redux/ui/actions';
import Localizer from '../localization';
import { StorageKeys } from '../localStorage/keys';
import LocalStorageManager from '../localStorage/localStorageManager';
import { Messenger, MessengerEvents } from '../messenger';
import { PIN_LENGTH } from '../parentalControl/constsAndTypes';
import { deleteLocalSettingsForProfile } from '../settings/settings';
import ProfilesManager from './profilesManager';
import { DEFAULT_AVATAR_IDS } from './typesAndConstants';

const createProfile = (avatarId: string): Profile => {
  return {
    avatarId,
    gameProfileId: null,
    id: crypto.randomUUID(),
    isDefault: false,
    isKid: false,
    name: '',
    ntvBucketId: null,
  };
};

const createDefaultProfile = (): Profile => {
  const profile = createProfile(getRandomUnusedAvatarId());
  profile.name = Localizer.localize('profiles.default_name');
  profile.isDefault = true;

  return profile;
};

const getFirstUnusedAvatarId = (): string => {
  const usedAvatarIds = ProfilesManager.getUsedAvatarIds();

  for (const avatarId of ProfilesManager.avatarIds) {
    if (!usedAvatarIds.has(avatarId)) {
      return avatarId;
    }
  }

  // Will never happen since max number of profiles is lesser than number of avatars
  return ProfilesManager.avatarIds[0];
};

const getRandomUnusedAvatarId = (): string => {
  const availableAvatarIds = ProfilesManager.getAvailableAvatars();
  return availableAvatarIds[Math.floor(Math.random() * availableAvatarIds.length)];
};

const avatarIdsToListOrDefault = (avatarIds: string | null): Array<string> => avatarIds?.split(',') ?? DEFAULT_AVATAR_IDS;

const getAvailableAvatarIds = (avatarIds: Array<string>, profiles: ProfileList, currentAvatarId?: string | null): Array<string> => {
  const availableAvatarIds = [...avatarIds];
  const usedAvatarIds = ProfilesManager.getUsedAvatarIds();

  return availableAvatarIds.filter((avatarId) => !usedAvatarIds.has(avatarId) || avatarId === currentAvatarId);
};

const isProfilePrivate = (profile: Profile): boolean => {
  const { pinCode } = profile;

  return typeof pinCode === 'string' && pinCode.length === PIN_LENGTH;
};

const isNameAlreadyUsed = (profiles: Profiles, profileId: string, name: string): boolean => {
  const nameStd = name.toLocaleLowerCase();

  if (profiles.list.some(({ id, name }) => name.toLocaleLowerCase() === nameStd && id !== profileId)) {
    Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.name_already_used'));
    return true;
  }

  return false;
};

const checkObsoleteProfiles = (profiles: Profiles): void => {
  const currentIds = new Set(profiles.list.map(({ id }) => id));
  const previousIds = LocalStorageManager.loadObject(StorageKeys.ProfileIds);

  const instance = ProfilesManager.getInstance();
  previousIds?.forEach((id) => {
    if (!currentIds.has(id)) {
      // Profile has been deleted on another device
      instance.dispatch(deleteSearchHistory(id));
      deleteLocalSettingsForProfile(id);
    }
  });

  // Save all profile Ids in order to compare with list of retrieved profiles at next startup
  LocalStorageManager.save(
    StorageKeys.ProfileIds,
    profiles.list.map(({ id }) => id),
  );
};

export {
  avatarIdsToListOrDefault,
  checkObsoleteProfiles,
  createDefaultProfile,
  createProfile,
  getAvailableAvatarIds,
  getFirstUnusedAvatarId,
  getRandomUnusedAvatarId,
  isNameAlreadyUsed,
  isProfilePrivate,
};
